import { LinearProgress } from '@mui/material'
import React, { useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useRoles } from '@/hooks/useRoles'
import { useAuthContext } from '@/providers/Global'
import E401 from '@/routes/_401/Index'

//	///////////////////////
//	MARK: EXPORTS
const LoggedInRouteInternal: React.FC<
  React.PropsWithChildren<{
    roles?: string[]
    ignoreSuperadmin?: boolean
  }>
> = ({ roles, ignoreSuperadmin, children }) => {
  const location = useLocation()
  const Auth = useAuthContext()

  const { loading, menuItems } = useRoles()

  const restricted = useMemo(() => {
    if (!roles) {
      return false
    }

    if (loading) {
      return true
    }

    for (const role of roles) {
      if (menuItems.has(role, ignoreSuperadmin)) {
        return false
      }
    }

    return true
  }, [ignoreSuperadmin, loading, menuItems, roles])

  if (!Auth.active) {
    return <Navigate to='/login' state={{ message: 'e.you_must_log_in', from: location }} replace />
  }

  if (loading) {
    return <LinearProgress />
  }

  if (restricted) {
    return <E401 roles={roles} />
  }

  return <>{children}</>
}

export const LoggedInRoute = React.memo(LoggedInRouteInternal) as typeof LoggedInRouteInternal

//	///////////////////////
//	MARK: HOC
export function withLoggedInRoute<P>(
  Component: React.FC<P>,
  roles?: string[],
  ignoreSuperadmin?: boolean,
): React.FC<P> {
  const value: React.FC<P> = (props) => (
    <LoggedInRoute roles={roles} ignoreSuperadmin={ignoreSuperadmin}>
      {/* @ts-expect-error */}
      <Component {...props} />
    </LoggedInRoute>
  )

  return value
}
